import {
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowLeftBar,
  IconCheck,
  IconCopy,
  IconInfoCircle,
  IconSparkles,
  IconTrash,
  IconWriting,
} from "@tabler/icons-react";
import { FormEvent, ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../../seo/Seo";
import {
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
} from "../../seo/utils";
import GetHelpForm from "../Shared/GetHelpForm";
import {
  cursiveStyleDefinitions,
  transformTextWithStyle,
} from "./cursiveUnicode";
import "./CursiveGeneratorPage.css";

const TOOL_PATH = "/tools/cursive-generator";
const TOOLS_PATH = "/tools";
const MAX_CHARACTERS = 300;
const seoTitle = "Cursive Generator | Free Cursive Text & Font Generator";
const seoDescription =
  "Use SubjectBuddy's free cursive generator to turn normal text into cursive-style writing. Generate cursive names, words, fonts, and copy-paste cursive text instantly.";

const faqs = [
  {
    question: "What is a cursive generator?",
    answer:
      "A cursive generator is a tool that converts normal text into cursive-style writing. You can use it to create cursive names, words, sentences, and copy-paste cursive text.",
  },
  {
    question: "How do I use this cursive text generator?",
    answer:
      "Type your text into the input box, click generate, choose a cursive style, and copy the result. You can paste the generated cursive text into supported apps and documents.",
  },
  {
    question: "Can I use this as a cursive font generator?",
    answer:
      "Yes. This tool works as a cursive font generator by showing different cursive-style versions of your text. Some outputs use Unicode characters so they can be copied and pasted.",
  },
  {
    question: "Can I generate cursive for my name?",
    answer:
      "Yes. You can use this as a cursive name generator by typing your name and choosing a cursive or signature-style output.",
  },
  {
    question: "How to write my name in cursive generator?",
    answer:
      "Enter your name in the tool and click generate. The generator will show your name in different cursive styles that you can use as inspiration or copy as text.",
  },
  {
    question: "How to sign my name in cursive generator?",
    answer:
      "To see signature-style ideas, type your full name or initials into the cursive generator and compare the available styles. Use the result as inspiration for practicing your own signature.",
  },
  {
    question: "Can I copy paste cursive text?",
    answer:
      "Yes. The tool creates copy paste cursive text using Unicode-style characters where possible. Some apps may display the text differently depending on font support.",
  },
  {
    question: "What is the difference between cursive font and cursive text?",
    answer:
      "A cursive font changes how text looks visually through styling, while cursive Unicode text can often be copied and pasted while keeping its special character style.",
  },
  {
    question: "Is this cursive generator free?",
    answer:
      "Yes. This cursive generator is free to use and works directly in your browser.",
  },
  {
    question: "Can this help me learn cursive handwriting?",
    answer:
      "This tool can help you preview cursive word shapes, but handwriting practice still requires writing letters by hand and learning how strokes connect.",
  },
];

const relatedWritingTools = [
  {
    title: "College Essay Editor",
    path: "/tools/college-essay-editor",
    description:
      "Use the College Essay Editor to check word count, structure, readability, and editing priorities.",
  },
  {
    title: "AI College Essay Reviewer",
    path: "/tools/ai-college-essay-reviewer",
    description:
      "Use the AI College Essay Reviewer to get instant feedback on clarity, tone, and structure.",
  },
  {
    title: "Speech Word Counter",
    path: "/tools/speech-word-counter",
    description:
      "Use the Speech Word Counter to estimate speaking time and count words for presentations.",
  },
  {
    title: "Word Counter",
    path: "/tools/word-counter",
    description:
      "Use the Word Counter to count words, characters, and paragraphs in your writing.",
  },
  {
    title: "Grammar Checker",
    path: "/tools/grammar-checker",
    description:
      "Use the Grammar Checker to improve grammar, punctuation, and sentence clarity.",
  },
  {
    title: "Paraphrasing Tool",
    path: "/tools/paraphrasing-tool",
    description:
      "Use the Paraphrasing Tool to rewrite sentences clearly while keeping the original meaning.",
  },
];

function buildGeneratedStyles(input: string) {
  return cursiveStyleDefinitions.map((style) => ({
    ...style,
    output: transformTextWithStyle(input, style),
  }));
}

function fallbackCopyText(text: string) {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "true");
  textArea.style.position = "absolute";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  fallbackCopyText(text);
}

function CursiveGeneratorPage() {
  const navigate = useNavigate();
  const [inputText, setInputText] = useState("");
  const [error, setError] = useState("");
  const [generatedText, setGeneratedText] = useState("");
  const [copiedKey, setCopiedKey] = useState("");

  const generatedStyles = generatedText ? buildGeneratedStyles(generatedText) : [];
  const exampleStyle = cursiveStyleDefinitions[0];
  const exampleRows = [
    ["My Name", transformTextWithStyle("My Name", exampleStyle), "Name style"],
    ["Thank You", transformTextWithStyle("Thank You", exampleStyle), "Note or message"],
    ["Study Notes", transformTextWithStyle("Study Notes", exampleStyle), "Heading"],
    [
      "Happy Birthday",
      transformTextWithStyle("Happy Birthday", exampleStyle),
      "Greeting",
    ],
    [
      "Subject Buddy",
      transformTextWithStyle("Subject Buddy", exampleStyle),
      "Creative text",
    ],
  ];

  async function handleCopy(text: string, key: string) {
    try {
      await copyText(text);
      setCopiedKey(key);
      window.setTimeout(() => {
        setCopiedKey((currentKey) => (currentKey === key ? "" : currentKey));
      }, 1600);
    } catch {
      setCopiedKey("");
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!inputText.trim()) {
      setError("Please enter text to generate cursive.");
      setGeneratedText("");
      return;
    }

    setError("");
    setGeneratedText(inputText.slice(0, MAX_CHARACTERS));
  }

  function handleClear() {
    setInputText("");
    setGeneratedText("");
    setError("");
    setCopiedKey("");
  }

  const softwareApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Cursive Generator",
    applicationCategory: "EducationalApplication",
    operatingSystem: "Web",
    description:
      "A free cursive generator that helps users create cursive text, names, words, and copy-paste cursive writing styles.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  };

  return (
    <Container fluid className="cursive-page" px={0}>
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={TOOL_PATH}
        ogTitle="Cursive Generator"
        ogDescription="Generate cursive text, names, words, and signature-style writing with this free online cursive font generator."
        keywords={[
          "cursive generator",
          "cursive font generator",
          "cursive text generator",
          "generate cursive",
          "cursive name generator",
          "cursive words",
          "copy paste cursive",
          "cursive script generator",
          "cursive font",
          "cursive writing copy and paste",
          "how to sign my name in cursive generator",
          "how to write cursive generator",
          "how to write in cursive generator",
          "how to write my name in cursive generator",
          "how to write my name in cursive signature generator",
        ]}
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: TOOLS_PATH },
            { name: "Cursive Generator", path: TOOL_PATH },
          ]),
          createFaqSchema(faqs),
          softwareApplicationSchema,
        ]}
      />

      <Box className="cursive-hero">
        <Container size="xl">
          <Stack gap="lg">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Anchor component={Link} to={TOOLS_PATH}>
                Tools
              </Anchor>
              <Text>Cursive Generator</Text>
            </Breadcrumbs>

            <Button
              color="violet"
              variant="subtle"
              leftSection={<IconArrowLeftBar />}
              onClick={() => navigate(TOOLS_PATH)}
              className="cursive-back-button"
            >
              Back to Tools
            </Button>

            <SimpleGrid
              cols={{ base: 1, lg: 2 }}
              spacing={36}
              className="cursive-hero-grid"
            >
              <Stack gap="lg" className="cursive-hero-intro">
                <Group gap="sm">
                  <Badge color="violet" size="lg" radius="sm">
                    Writing Tool
                  </Badge>
                  <Badge color="blue" size="lg" radius="sm" variant="light">
                    Free Student Tool
                  </Badge>
                </Group>

                <Title order={1} className="cursive-title">
                  Cursive Generator
                </Title>

                <Text size="lg" c="dimmed" className="cursive-hero-copy">
                  Use this cursive generator to turn normal text into stylish
                  cursive writing instantly. Type your name, word, or sentence,
                  choose a cursive font style, and copy-paste the result
                  anywhere you need.
                </Text>

                <Text c="dimmed" className="cursive-helper-copy">
                  This tool works as a cursive text generator, cursive font
                  generator, and cursive name generator for students, notes,
                  projects, social media, and creative writing.
                </Text>

                <Alert
                  icon={<IconInfoCircle size={18} />}
                  color="violet"
                  radius="lg"
                  variant="light"
                >
                  Generated outputs use Unicode cursive-style characters where
                  possible, so supported apps can keep the copied style.
                </Alert>
              </Stack>

              <section
                aria-labelledby="cursive-generator-heading"
                className="cursive-generator-section"
              >
                <Paper
                  withBorder
                  radius="xl"
                  p="xl"
                  className="cursive-tool-card"
                >
                  <form onSubmit={handleSubmit} noValidate>
                    <Stack gap="lg">
                      <Stack gap={4}>
                        <Title id="cursive-generator-heading" order={2}>
                          Generate Cursive Text
                        </Title>
                        <Text c="dimmed">
                          Enter up to 300 characters and generate cursive
                          words, names, sentences, and signature-style text.
                        </Text>
                      </Stack>

                      <Stack gap="sm">
                        <Group justify="space-between" align="center">
                          <Text component="label" htmlFor="cursive-input" fw={800}>
                            Enter your text
                          </Text>
                          <Text size="sm" c="dimmed">
                            {MAX_CHARACTERS - inputText.length} characters left
                          </Text>
                        </Group>
                        <Textarea
                          id="cursive-input"
                          value={inputText}
                          onChange={(event) => {
                            setInputText(
                              event.currentTarget.value.slice(0, MAX_CHARACTERS),
                            );
                            setError("");
                          }}
                          minRows={5}
                          maxRows={8}
                          placeholder="Type your name or words here"
                          aria-label="Enter your text"
                          error={error}
                        />
                      </Stack>

                      <Group gap="md" className="cursive-form-actions">
                        <Button
                          type="submit"
                          size="lg"
                          radius="lg"
                          color="violet"
                          leftSection={<IconWriting />}
                        >
                          Generate Cursive
                        </Button>
                        <Button
                          type="button"
                          size="lg"
                          radius="lg"
                          variant="light"
                          color="gray"
                          leftSection={<IconTrash />}
                          onClick={handleClear}
                        >
                          Clear
                        </Button>
                      </Group>
                    </Stack>
                  </form>
                </Paper>
              </section>
            </SimpleGrid>

            {generatedStyles.length ? (
              <section className="cursive-results-panel" aria-live="polite">
                <Group
                  justify="space-between"
                  align="flex-start"
                  gap="md"
                  className="cursive-results-header"
                >
                  <Stack gap={4}>
                    <Group gap="xs">
                      <ThemeIcon
                        color="violet"
                        variant="light"
                        radius="xl"
                        size="lg"
                      >
                        <IconSparkles size={18} />
                      </ThemeIcon>
                      <Text fw={900}>Generated styles</Text>
                    </Group>
                    <Text size="sm" c="dimmed">
                      Compare cursive variants, then copy one style or all
                      styles at once.
                    </Text>
                  </Stack>
                  <Button
                    type="button"
                    size="md"
                    radius="lg"
                    variant="outline"
                    color="violet"
                    leftSection={
                      copiedKey === "all" ? (
                        <IconCheck size={18} />
                      ) : (
                        <IconCopy size={18} />
                      )
                    }
                    onClick={() =>
                      handleCopy(
                        generatedStyles
                          .map((style) => `${style.name}: ${style.output}`)
                          .join("\n"),
                        "all",
                      )
                    }
                  >
                    {copiedKey === "all" ? "Copied!" : "Copy All Styles"}
                  </Button>
                </Group>

                <SimpleGrid
                  cols={{ base: 1, sm: 2, lg: 3, xl: 4 }}
                  spacing="md"
                  className="cursive-output-grid"
                >
                  {generatedStyles.map((style) => (
                    <Paper
                      key={style.id}
                      withBorder
                      radius="lg"
                      p="lg"
                      className="cursive-output-card"
                    >
                      <Stack gap="sm">
                        <Group
                          justify="space-between"
                          align="flex-start"
                          className="cursive-output-card-header"
                        >
                          <Stack gap={2} className="cursive-output-card-title">
                            <Text fw={800} c="violet">
                              {style.name}
                            </Text>
                            <Text size="sm" c="dimmed">
                              {style.previewLabel}
                            </Text>
                          </Stack>
                          <Button
                            type="button"
                            size="compact-md"
                            radius="xl"
                            variant="light"
                            color="violet"
                            leftSection={
                              copiedKey === style.id ? (
                                <IconCheck size={16} />
                              ) : (
                                <IconCopy size={16} />
                              )
                            }
                            onClick={() => handleCopy(style.output, style.id)}
                            aria-label={`Copy ${style.name}`}
                          >
                            {copiedKey === style.id ? "Copied!" : "Copy"}
                          </Button>
                        </Group>

                        <Text size="sm" c="dimmed">
                          {style.description}
                        </Text>

                        <Paper
                          radius="md"
                          p="md"
                          className="cursive-output-preview"
                        >
                          <Text className="cursive-output-text">
                            {style.output}
                          </Text>
                        </Paper>

                        <Stack gap={4}>
                          <Text size="xs" fw={700} c="dimmed">
                            Font preview only
                          </Text>
                          <Text className="cursive-font-preview">
                            {generatedText}
                          </Text>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </SimpleGrid>
              </section>
            ) : null}
          </Stack>
        </Container>
      </Box>

      <Container size="xl" py={48}>
        <div className="cursive-layout">
          <main className="cursive-main">
            <article className="cursive-content">
              <SeoSection title="What Is a Cursive Generator?">
                <Text>
                  A cursive generator is an online tool that converts regular
                  text into cursive-style writing. You can use it to generate
                  cursive words, names, sentences, and signature-style text that
                  can be copied and pasted.
                </Text>
                <Text>
                  This page works as a cursive text generator and cursive font
                  generator for users who want quick cursive writing online.
                </Text>
              </SeoSection>

              <SeoSection title="How to Use This Cursive Text Generator">
                <Text>
                  To use this cursive text generator, type your text in the
                  input box, click generate, and choose the cursive style you
                  like. You can then copy the cursive text and paste it into
                  notes, bios, assignments, social posts, or design projects.
                </Text>
                <ol className="cursive-steps">
                  <li>Enter your name, word, or sentence.</li>
                  <li>Click "Generate Cursive."</li>
                  <li>Choose your favorite cursive style.</li>
                  <li>Click copy.</li>
                  <li>Paste the cursive text wherever you need it.</li>
                </ol>
              </SeoSection>

              <SeoSection title="Cursive Font Generator for Names and Words">
                <Text>
                  This cursive font generator can help you create stylish
                  names, cursive words, and short phrases. It is useful for
                  students, creative projects, handwriting inspiration, and
                  social media captions.
                </Text>
                <Text>
                  If you want to generate cursive for a name, type your name
                  into the tool and choose a style that looks closest to
                  handwriting.
                </Text>
              </SeoSection>

              <SeoSection title="Cursive Name Generator">
                <Text>
                  A cursive name generator helps you see how your name may look
                  in a cursive or signature-style format. You can try different
                  versions of your name, initials, or full name before copying
                  the result.
                </Text>
                <Text>
                  Example: If your name is Sophia, the tool can show different
                  cursive styles for Sophia, Sophia M., or your full
                  signature-style name.
                </Text>
              </SeoSection>

              <SeoSection title="Copy Paste Cursive Text">
                <Text>
                  Copy paste cursive text uses special Unicode characters that
                  look like cursive writing. After generating your text, click
                  the copy button and paste it into supported apps, notes,
                  documents, or social media platforms.
                </Text>
                <Text>
                  Some platforms may display Unicode cursive differently
                  depending on the device, browser, or app.
                </Text>
              </SeoSection>

              <SeoSection title="Cursive Script Generator">
                <Text>
                  A cursive script generator creates text that looks similar to
                  handwritten script. Script-style text is often used for names,
                  headings, invitations, personal notes, and creative writing
                  designs.
                </Text>
              </SeoSection>

              <SeoSection title="How to Write in Cursive Generator">
                <Text>
                  If you are searching for how to write in cursive generator,
                  this tool can show your words in cursive-style text instantly.
                  It can help you understand how letters may connect visually in
                  cursive writing, but it should be used as a digital preview
                  rather than handwriting practice alone.
                </Text>
                <Text>
                  People also search for how to write cursive generator when
                  they want a quick way to compare letter shapes before writing
                  them by hand.
                </Text>
              </SeoSection>

              <SeoSection title="How to Sign My Name in Cursive Generator">
                <Text>
                  Students and users often search for how to sign my name in
                  cursive generator when they want signature inspiration. Type
                  your name into the cursive name generator and compare
                  different styles before practicing your own handwritten
                  signature.
                </Text>
                <Text>
                  If you are wondering how to write my name in cursive
                  signature generator, the Signature Style card is the quickest
                  place to start before refining your own handwritten version.
                </Text>
              </SeoSection>

              <SeoSection title="Cursive Writing Copy and Paste Examples">
                <ResponsiveTable
                  headers={["Normal Text", "Cursive Style Example", "Use Case"]}
                  rows={exampleRows}
                />
              </SeoSection>

              <SeoSection title="Best Uses of a Cursive Generator">
                <ul className="cursive-bullets">
                  <li>Creating cursive names</li>
                  <li>Making stylish notes</li>
                  <li>Designing social media bios</li>
                  <li>Practicing cursive word shapes</li>
                  <li>Creating headings for projects</li>
                  <li>Testing signature-style text</li>
                  <li>Making creative captions</li>
                </ul>
              </SeoSection>

              <SeoSection title="Related Writing Tools">
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  {relatedWritingTools.map((tool) => (
                    <Paper
                      key={tool.path}
                      component={Link}
                      to={tool.path}
                      withBorder
                      radius="lg"
                      p="lg"
                      className="cursive-related-card"
                    >
                      <Stack gap="xs">
                        <Text fw={800} c="violet">
                          {tool.title}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {tool.description}
                        </Text>
                      </Stack>
                    </Paper>
                  ))}
                </SimpleGrid>
              </SeoSection>

              <SeoSection title="Related Student Support">
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  <Paper
                    component={Link}
                    to="/services/essay-editing-help"
                    withBorder
                    radius="lg"
                    p="lg"
                    className="cursive-related-card"
                  >
                    <Stack gap="xs">
                      <Text fw={800} c="violet">
                        Essay Editing Help
                      </Text>
                      <Text size="sm" c="dimmed">
                        Get support improving essay structure, clarity, and
                        final polish after using the cursive generator.
                      </Text>
                    </Stack>
                  </Paper>
                  <Paper
                    component={Link}
                    to="/assignment-help"
                    withBorder
                    radius="lg"
                    p="lg"
                    className="cursive-related-card"
                  >
                    <Stack gap="xs">
                      <Text fw={800} c="violet">
                        Assignment Help
                      </Text>
                      <Text size="sm" c="dimmed">
                        Work with SubjectBuddy support when a creative student
                        project needs clearer planning or academic guidance.
                      </Text>
                    </Stack>
                  </Paper>
                </SimpleGrid>
              </SeoSection>

              <SeoSection title="Cursive Generator FAQs">
                <Stack gap="md">
                  {faqs.map((faq) => (
                    <Paper key={faq.question} withBorder radius="lg" p="lg">
                      <Title order={3}>{faq.question}</Title>
                      <Text c="dimmed">{faq.answer}</Text>
                    </Paper>
                  ))}
                </Stack>
              </SeoSection>
            </article>
          </main>

          <aside className="cursive-sidebar" aria-label="Study support form">
            <div className="cursive-sidebar-sticky">
              <Stack gap="md">
                <Paper withBorder radius="xl" p="lg">
                  <Stack gap="xs">
                    <Group gap="sm">
                      <ThemeIcon color="violet" variant="light">
                        <IconSparkles size={20} />
                      </ThemeIcon>
                      <Text size="sm" tt="uppercase" fw={800} c="violet">
                        Writing support
                      </Text>
                    </Group>
                    <Title order={3}>Need Help With Writing Projects?</Title>
                    <Text c="dimmed" size="sm">
                      Share your writing task, study goal, or deadline and we
                      will help you plan the next step.
                    </Text>
                  </Stack>
                </Paper>
                <Paper withBorder radius="xl" p={0} className="cursive-help-form">
                  <GetHelpForm formSize="sm" flatForm />
                </Paper>
              </Stack>
            </div>
          </aside>
        </div>
      </Container>
    </Container>
  );
}

function SeoSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="cursive-seo-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </section>
  );
}

function ResponsiveTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}) {
  return (
    <Box className="cursive-table-wrap">
      <Table striped highlightOnHover withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            {headers.map((header) => (
              <Table.Th key={header}>{header}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.join("-")}>
              {row.map((cell) => (
                <Table.Td key={cell}>
                  <Text className="cursive-table-cell">{cell}</Text>
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}

export default CursiveGeneratorPage;
